<script setup>
// ============================================================================
// BonBoZ: TỔNG QUAN — dựng lại theo đúng bố cục Pancake.
//
// BỐ CỤC, từ trên xuống, đúng thứ tự Pancake:
//   1. Tổng quan về hoạt động — cột (tương tác) + 2 đường (khách mới, đơn chốt)
//   2. Hai cột:
//      trái  — Tổng quan về trang · Top thẻ hội thoại · Đánh giá
//      phải  — Tương tác (4 ô + đường nhỏ) · Nhân viên · Tổng đài + Theo giờ
//
// MỘT LƯỢT GỌI cho cả trang (`/bonboz/hieu_suat/tong_quan`). Bản trước gọi bốn
// nguồn rồi cộng ở giao diện, mỗi nguồn cắt kỳ một kiểu — lý do đầy đủ ở đầu
// `Bonboz::TongQuanBuilder`.
//
// GIỮ LẠI SO VỚI PANCAKE, vì người dùng đã yêu cầu riêng trước đó:
//   · dải chọn khoảng ngày và `PhamVi` (đang xem những trang nào)
//   · nút "Tải xuống Excel"
//   · dải "NGAY LÚC NÀY" — số sống, không theo khoảng ngày
// Màu và nền dùng biến theme của Chatwoot chứ không phải mã màu cứng của
// Pancake: sản phẩm này có nền tối, mã cứng thì nền tối đọc không ra.
// ============================================================================
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { useSelectedPages } from 'dashboard/composables/useSelectedPages';
import HieuSuatAPI from 'dashboard/api/bonboz/hieuSuat';
import { useAlert } from 'dashboard/composables';
import { ngay } from 'dashboard/helper/dinhDangNgay';
import { mauCss } from 'dashboard/helper/mauBieuDo';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  taiXuongExcel,
  tenFileThongKe,
} from 'dashboard/helper/bonboz/xuatExcel';
import OverviewReportFilters from '../components/OverviewReportFilters.vue';
import PhamVi from './PhamVi.vue';
import SectionCard from './SectionCard.vue';
import BieuDoTongQuan from './BieuDoTongQuan.vue';
import BieuDoTheoGio from './BieuDoTheoGio.vue';
import BieuDoTron from './BieuDoTron.vue';
import DuongNho from './DuongNho.vue';

const { t } = useI18n();
// Moi con so tren trang nay chi duoc dem tren nhung trang dang chon o bang
// dieu khien — ke ca dai "ngay luc nay".
const { selectedInboxIds } = useSelectedPages();

const tu = ref(null);
const den = ref(null);
const soLieu = ref(null);
const soLieuSong = ref({});
const dangTai = ref(true);

const doiBoLoc = async ({ from, to }) => {
  tu.value = from;
  den.value = to;
  dangTai.value = true;
  try {
    const { data } = await HieuSuatAPI.tongQuan({
      since: from,
      until: to,
      inboxIds: selectedInboxIds.value,
    });
    soLieu.value = data;
  } catch {
    soLieu.value = null;
    useAlert(t('BONBOZ.TONG_QUAN.LOI_TAI'));
  } finally {
    dangTai.value = false;
  }
};

// Số sống chạy riêng, KHÔNG dính bộ lọc ngày và không mờ đi khi phần trên đang
// tải — nó trả lời "ngay bây giờ còn gì trên bàn".
//
// Gọi endpoint BonBoZ chứ không dùng `fetchAccountConversationMetric` của
// upstream: action đó đếm toàn tài khoản và không lọc được theo hộp thư, nên
// dải này sẽ nói về những trang mà phần còn lại của màn hình đang không hiện.
const taiSoSong = async () => {
  try {
    const { data } = await HieuSuatAPI.hienTai({
      inboxIds: selectedInboxIds.value,
    });
    soLieuSong.value = data;
  } catch {
    // Số sống hỏng thì im lặng: phần còn lại của trang vẫn đọc được, và lượt
    // làm mới sau vài giây sẽ thử lại.
  }
};

// Lua chon trang nam trong `ui_settings` cua nguoi dung, tai ve SAU khi trang da
// mount — lan tai dau tien co the chay khi mang con rong (= tat ca). Watch de
// tai lai ngay khi lua chon that su toi.
watch(selectedInboxIds, () => {
  if (tu.value && den.value) doiBoLoc({ from: tu.value, to: den.value });
  taiSoSong();
});

const { startRefetching } = useLiveRefresh(taiSoSong);
onMounted(() => {
  taiSoSong();
  startRefetching();
});

// ------------------------------------------------------------- biểu đồ chính
const hoatDong = computed(() => soLieu.value?.hoat_dong || []);
const nhanTruc = computed(() =>
  hoatDong.value.map(d => ngay(new Date(d.moc * 1000), 'ngayThang'))
);
const coHoatDong = computed(() =>
  hoatDong.value.some(d => d.tuong_tac || d.khach_moi || d.don_chot)
);

// Chú giải vẽ bằng HTML thay vì legend của chart.js: dịch được, đặt đúng chỗ
// Pancake đặt, và không bấm nhầm làm ẩn mất một chuỗi.
const chuGiai = [
  { khoa: 'tuong_tac', nhan: 'BONBOZ.TONG_QUAN.TONG_TUONG_TAC', mau: 'xong' },
  { khoa: 'khach_moi', nhan: 'BONBOZ.TONG_QUAN.KHACH_HANG_MOI', mau: 'tre' },
  { khoa: 'don_chot', nhan: 'BONBOZ.TONG_QUAN.SO_DON_CHOT', mau: 'mo' },
];

// ------------------------------------------------------------------ các khối
const trang = computed(
  () => soLieu.value?.trang || { tin_nhan: 0, binh_luan: 0 }
);

const oTrang = computed(() => [
  {
    khoa: 'tin',
    nhan: 'BONBOZ.TONG_QUAN.TIN_TOI_TRANG',
    so: trang.value.tin_nhan,
    icon: 'i-lucide-mail',
    bien: '--bonboz-mo',
  },
  {
    khoa: 'binh_luan',
    nhan: 'BONBOZ.TONG_QUAN.BINH_LUAN_TOI_TRANG',
    so: trang.value.binh_luan,
    icon: 'i-lucide-message-circle',
    bien: '--bonboz-tre',
    // Kênh Messenger của Chatwoot không nhận bình luận trên bài viết, nên ô
    // này còn 0 cho tới khi có nguồn. Nói ra bằng tooltip chứ không để người
    // đọc tự đoán là "hôm nay không ai bình luận".
    gt: 'BONBOZ.TONG_QUAN.GT_BINH_LUAN',
  },
]);

const oTuongTac = computed(() => {
  const x = soLieu.value?.tuong_tac;
  if (!x) return [];
  return [
    {
      khoa: 'tong',
      nhan: 'BONBOZ.TONG_QUAN.TONG_TUONG_TAC',
      ...x.tong,
      mau: '--bonboz-nguoi',
    },
    {
      khoa: 'tin',
      nhan: 'BONBOZ.TONG_QUAN.TIN_NHAN',
      ...x.tin_nhan,
      mau: '--bonboz-mo',
    },
    {
      khoa: 'binh_luan',
      nhan: 'BONBOZ.TONG_QUAN.BINH_LUAN',
      ...x.binh_luan,
      mau: '--bonboz-cho',
      gt: 'BONBOZ.TONG_QUAN.GT_BINH_LUAN',
    },
    {
      khoa: 'ht',
      nhan: 'BONBOZ.TONG_QUAN.HT_TIN_NHAN_MOI',
      ...x.ht_moi,
      mau: '--bonboz-xong',
    },
  ];
});

const nhanVien = computed(() => soLieu.value?.nhan_vien || []);
const theHoiThoai = computed(() => soLieu.value?.the || []);

// Ba hạng đầu đeo huy chương, đúng như Pancake. Từ hạng 4 trở đi chỉ còn số thứ
// tự — huy chương cho tất cả thì không còn là huy chương.
const MAU_HUY_CHUONG = ['#E3A008', '#9CA3AF', '#B45309'];
const huyChuong = i => MAU_HUY_CHUONG[i] || null;

const danhGia = computed(
  () => soLieu.value?.danh_gia || { trung_binh: 0, tot: 0, kha: 0, kem: 0 }
);
// Miếng của vòng tròn. Không có đánh giá nào thì vẫn phải vẽ một vòng xám —
// canvas trống trông như biểu đồ hỏng.
const miengDanhGia = computed(() => {
  const d = danhGia.value;
  const co = d.tot + d.kha + d.kem;
  if (!co) return [{ tag: '', so: 1, nen: mauCss('--bonboz-mo', 0.12) }];
  return [
    {
      tag: t('BONBOZ.TONG_QUAN.SAO_TOT'),
      so: d.tot,
      nen: mauCss('--bonboz-xong'),
    },
    {
      tag: t('BONBOZ.TONG_QUAN.SAO_KHA'),
      so: d.kha,
      nen: mauCss('--bonboz-cho'),
    },
    {
      tag: t('BONBOZ.TONG_QUAN.SAO_KEM'),
      so: d.kem,
      nen: mauCss('--bonboz-tre'),
    },
  ];
});
const dongDanhGia = computed(() => [
  {
    khoa: 'tot',
    nhan: 'BONBOZ.TONG_QUAN.SAO_TOT',
    so: danhGia.value.tot,
    nen: 'rgb(var(--bonboz-xong))',
  },
  {
    khoa: 'kha',
    nhan: 'BONBOZ.TONG_QUAN.SAO_KHA',
    so: danhGia.value.kha,
    nen: 'rgb(var(--bonboz-cho))',
  },
  {
    khoa: 'kem',
    nhan: 'BONBOZ.TONG_QUAN.SAO_KEM',
    so: danhGia.value.kem,
    nen: 'rgb(var(--bonboz-tre))',
  },
]);

const tongDai = computed(
  () => soLieu.value?.tong_dai || { tong: 0, den: 0, di: 0, thay_doi: null }
);
const miengTongDai = computed(() => {
  const x = tongDai.value;
  if (!x.tong) return [{ tag: '', so: 1, nen: mauCss('--bonboz-mo', 0.12) }];
  return [
    {
      tag: t('BONBOZ.TONG_QUAN.GOI_DEN'),
      so: x.den,
      nen: mauCss('--bonboz-xong'),
    },
    { tag: t('BONBOZ.TONG_QUAN.GOI_DI'), so: x.di, nen: mauCss('--bonboz-mo') },
  ];
});

const theoGio = computed(() => soLieu.value?.theo_gio || []);

// Mũi tên là HÌNH chứ không phải màu, nên người không phân biệt được màu vẫn
// đọc được "cao hơn hay thấp hơn kỳ trước". `null` = không có kỳ trước để so,
// lúc đó im lặng thay vì hiện 0%.
const nhipTang = phanTram => {
  if (phanTram === null || phanTram === undefined) return null;
  if (phanTram > 0) return { mui: '▲', huong: 'text-n-teal-11' };
  if (phanTram < 0) return { mui: '▼', huong: 'text-n-ruby-11' };
  return { mui: '', huong: 'text-n-slate-11' };
};

// --------------------------------------------------------- việc đang treo
// unattended và unassigned là hai tập CHỒNG NHAU, không lồng nhau
// (conversation.rb:86 và :92) — nên tuyệt đối không viết "trong đó".
const dangCho = computed(() => Number(soLieuSong.value?.unattended ?? 0));
const chuaAiNhan = computed(() => Number(soLieuSong.value?.unassigned ?? 0));
const sachViec = computed(() => dangCho.value === 0 && chuaAiNhan.value === 0);

// ------------------------------------------------------------------ Excel
// Trang này không có bảng nào, nên file là bản chữ của những gì đang đứng trên
// màn hình: mỗi ô một dòng, rồi tới hai bảng xếp hạng.
const xuatExcel = () => {
  const cacHang = [
    [t('BONBOZ.XUAT.CHI_SO'), t('BONBOZ.XUAT.GIA_TRI')],
    ...oTuongTac.value.map(o => [t(o.nhan), o.so]),
    [t('BONBOZ.TONG_QUAN.TIN_TOI_TRANG'), trang.value.tin_nhan],
    [t('BONBOZ.TONG_QUAN.BINH_LUAN_TOI_TRANG'), trang.value.binh_luan],
    [t('BONBOZ.TONG_QUAN.DIEM_TRUNG_BINH'), danhGia.value.trung_binh],
    [t('BONBOZ.TONG_QUAN.TONG_CUOC_GOI'), tongDai.value.tong],
  ];
  if (nhanVien.value.length) {
    cacHang.push(
      [],
      [t('BONBOZ.NHAN_VIEN.COT_TEN'), t('BONBOZ.TONG_QUAN.LUOT')]
    );
    nhanVien.value.forEach(n => cacHang.push([n.ten, n.luot]));
  }
  if (theHoiThoai.value.length) {
    cacHang.push([], [t('BONBOZ.THE_TK.COT_THE'), t('BONBOZ.THE_TK.COT_SO')]);
    theHoiThoai.value.forEach(x => cacHang.push([x.ten, x.so]));
  }
  // Từng ngày của biểu đồ trên cùng: biểu đồ đọc được xu hướng, file mới đọc
  // được con số của đúng một ngày.
  if (hoatDong.value.length) {
    cacHang.push(
      [],
      [
        t('BONBOZ.TRANG_TK.COT_NGAY'),
        t('BONBOZ.TONG_QUAN.TONG_TUONG_TAC'),
        t('BONBOZ.TONG_QUAN.KHACH_HANG_MOI'),
        t('BONBOZ.TONG_QUAN.SO_DON_CHOT'),
      ]
    );
    hoatDong.value.forEach(d =>
      cacHang.push([
        ngay(new Date(d.moc * 1000), 'ngayDay'),
        d.tuong_tac,
        d.khach_moi,
        d.don_chot,
      ])
    );
  }
  taiXuongExcel(
    tenFileThongKe('thong-ke-tong-quan', tu.value, den.value),
    cacHang
  );
};
</script>

<template>
  <div class="flex flex-col w-full gap-4 mx-auto max-w-6xl">
    <OverviewReportFilters :disabled="dangTai" @filter-change="doiBoLoc" />

    <div class="flex flex-wrap items-start justify-between gap-2">
      <PhamVi />
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-download"
        :label="$t('BONBOZ.XUAT.NUT')"
        :disabled="dangTai"
        class="ms-auto"
        @click="xuatExcel"
      />
    </div>

    <!-- ================= 1. TỔNG QUAN VỀ HOẠT ĐỘNG ================= -->
    <SectionCard
      :title="$t('BONBOZ.TONG_QUAN.HOAT_DONG')"
      :description="$t('BONBOZ.TONG_QUAN.HOAT_DONG_MO_TA')"
    >
      <div v-if="dangTai" class="rounded-lg h-72 bg-n-alpha-2 animate-pulse" />
      <p
        v-else-if="!coHoatDong"
        class="m-0 py-24 text-sm text-center text-n-slate-11"
      >
        {{ $t('BONBOZ.BIEU_DO.CHUA_CO') }}
      </p>
      <template v-else>
        <div class="h-72">
          <BieuDoTongQuan
            :nhan="nhanTruc"
            :tuong-tac="hoatDong.map(d => d.tuong_tac)"
            :khach-moi="hoatDong.map(d => d.khach_moi)"
            :don-chot="hoatDong.map(d => d.don_chot)"
            :ten-tuong-tac="$t('BONBOZ.TONG_QUAN.TONG_TUONG_TAC')"
            :ten-khach-moi="$t('BONBOZ.TONG_QUAN.KHACH_HANG_MOI')"
            :ten-don-chot="$t('BONBOZ.TONG_QUAN.SO_DON_CHOT')"
          />
        </div>
        <!-- Chú giải giữa, dưới khung — đúng chỗ Pancake đặt. -->
        <div
          class="flex flex-wrap justify-center gap-5 mt-3 text-xs text-n-slate-11"
        >
          <span
            v-for="c in chuGiai"
            :key="c.khoa"
            class="flex items-center gap-1.5"
          >
            <span
              class="rounded-full size-2.5 shrink-0"
              :style="{ background: `rgb(var(--bonboz-${c.mau}))` }"
              aria-hidden="true"
            />
            {{ $t(c.nhan) }}
          </span>
        </div>
      </template>
    </SectionCard>

    <!-- ================= 2. HAI CỘT ================= -->
    <div class="grid gap-4 lg:grid-cols-3 lg:items-start">
      <!-- ---------------- CỘT TRÁI ---------------- -->
      <div class="flex flex-col gap-4">
        <!-- Tổng quan về trang -->
        <SectionCard
          :title="$t('BONBOZ.TONG_QUAN.VE_TRANG')"
          :description="$t('BONBOZ.TONG_QUAN.VE_TRANG_MO_TA')"
          :to="{ name: 'conversation_reports' }"
        >
          <ul class="flex flex-col gap-4 p-0 m-0 list-none">
            <li
              v-for="o in oTrang"
              :key="o.khoa"
              v-tooltip.top="o.gt ? $t(o.gt) : null"
              class="flex items-center gap-3"
            >
              <span
                class="grid rounded-lg size-11 shrink-0 place-items-center"
                :style="{ background: `rgb(var(${o.bien}) / 0.12)` }"
              >
                <span
                  class="block size-5"
                  :class="o.icon"
                  :style="{ color: `rgb(var(${o.bien}))` }"
                  aria-hidden="true"
                />
              </span>
              <span class="min-w-0">
                <span class="block text-sm truncate text-n-slate-11">
                  {{ $t(o.nhan) }}
                </span>
                <span
                  v-if="dangTai"
                  class="block w-12 h-6 mt-1 rounded bg-n-alpha-2 animate-pulse"
                />
                <span
                  v-else
                  class="block text-2xl font-semibold tabular-nums text-n-slate-12"
                >
                  {{ o.so.toLocaleString() }}
                </span>
              </span>
            </li>
          </ul>
        </SectionCard>

        <!-- Top thẻ hội thoại -->
        <SectionCard
          :title="$t('BONBOZ.TONG_QUAN.TOP_THE')"
          :description="$t('BONBOZ.TONG_QUAN.TOP_THE_MO_TA')"
          :to="{ name: 'label_reports_index' }"
        >
          <div
            v-if="dangTai"
            class="h-20 rounded-lg bg-n-alpha-2 animate-pulse"
          />
          <p
            v-else-if="!theHoiThoai.length"
            class="m-0 text-sm text-n-slate-11"
          >
            {{ $t('BONBOZ.TONG_QUAN.CHUA_CO_TAG') }}
          </p>
          <ul v-else class="flex flex-col gap-3 p-0 m-0 list-none">
            <li
              v-for="x in theHoiThoai"
              :key="x.ten"
              class="flex items-center gap-2"
            >
              <!-- Màu chấm là MÀU NGƯỜI DÙNG ĐẶT cho nhãn ở Cài đặt → Nhãn:
                   nhìn thẻ ở đâu trong sản phẩm cũng phải ra cùng một màu. -->
              <span
                class="rounded-full size-2.5 shrink-0"
                :style="{ background: x.mau || 'rgb(var(--bonboz-mo))' }"
                aria-hidden="true"
              />
              <span class="flex-1 text-sm truncate text-n-slate-12">
                {{ x.ten }}
              </span>
              <span
                class="text-sm font-medium tabular-nums shrink-0"
                :style="{ color: x.mau || undefined }"
              >
                {{ x.so }}
              </span>
            </li>
          </ul>
        </SectionCard>

        <!-- Đánh giá -->
        <SectionCard
          :title="$t('BONBOZ.TONG_QUAN.DANH_GIA')"
          :description="$t('BONBOZ.TONG_QUAN.DANH_GIA_MO_TA')"
          :to="{ name: 'csat_reports' }"
        >
          <div class="relative grid mx-auto size-44 place-items-center">
            <div class="absolute inset-0">
              <BieuDoTron :dong="miengDanhGia" />
            </div>
            <!-- Chữ giữa là HTML ĐÈ lên canvas — dịch và style dễ hơn plugin
                 vẽ chữ của chart.js nhiều. -->
            <div
              class="absolute inset-0 grid px-8 text-center pointer-events-none place-content-center"
            >
              <span class="text-xs leading-4 text-n-slate-11">
                {{ $t('BONBOZ.TONG_QUAN.DIEM_TRUNG_BINH') }}
              </span>
              <span
                class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12"
              >
                {{ danhGia.trung_binh.toFixed(1) }}
                <span
                  class="inline-block align-baseline i-lucide-star size-4 text-n-amber-11"
                  aria-hidden="true"
                />
              </span>
            </div>
          </div>

          <p
            v-if="nhipTang(danhGia.thay_doi)"
            class="flex items-center justify-center gap-1 m-0 mt-2 text-xs"
            :class="nhipTang(danhGia.thay_doi).huong"
          >
            <span aria-hidden="true">{{ nhipTang(danhGia.thay_doi).mui }}</span>
            {{ Math.abs(danhGia.thay_doi) }}%
          </p>

          <ul class="flex flex-col gap-2 p-0 m-0 mt-3 list-none">
            <li
              v-for="d in dongDanhGia"
              :key="d.khoa"
              class="flex items-center gap-2"
            >
              <span
                class="rounded size-3 shrink-0"
                :style="{ background: d.nen }"
                aria-hidden="true"
              />
              <span class="flex-1 text-sm truncate text-n-slate-11">
                {{ $t(d.nhan) }}
              </span>
              <span class="text-sm tabular-nums text-n-slate-12">
                {{ d.so }}
              </span>
            </li>
          </ul>
        </SectionCard>
      </div>

      <!-- ---------------- CỘT PHẢI ---------------- -->
      <div class="flex flex-col gap-4 lg:col-span-2">
        <!-- Dải "ngay lúc này" — số sống, KHÔNG theo khoảng ngày đã chọn. -->
        <section
          class="py-3 pl-4 border-l-4 rounded-r-lg bonboz-dai-viec"
          :class="{ 'la-sach': sachViec }"
        >
          <p class="flex items-center gap-2 m-0 text-xs text-n-slate-11">
            <span class="bonboz-cham bonboz-cham-song" aria-hidden="true" />
            {{ $t('BONBOZ.TONG_QUAN.NGAY_LUC_NAY') }}
          </p>
          <p v-if="sachViec" class="mt-1 mb-0 text-base text-n-teal-11">
            {{ $t('BONBOZ.TONG_QUAN.SACH_VIEC') }}
          </p>
          <div v-else class="flex flex-wrap gap-6 mt-1">
            <p class="m-0 text-base text-n-slate-12">
              <span class="text-2xl font-medium tabular-nums">
                {{ dangCho }}
              </span>
              {{ $t('BONBOZ.TONG_QUAN.DANG_CHO') }}
            </p>
            <p class="m-0 text-base text-n-slate-12">
              <span class="text-2xl font-medium tabular-nums">
                {{ chuaAiNhan }}
              </span>
              {{ $t('BONBOZ.TONG_QUAN.CHUA_AI_NHAN') }}
            </p>
          </div>
        </section>

        <!-- Tương tác -->
        <SectionCard
          :title="$t('BONBOZ.TONG_QUAN.TUONG_TAC')"
          :description="$t('BONBOZ.TONG_QUAN.TUONG_TAC_MO_TA')"
          :to="{ name: 'conversation_reports' }"
          :link-label="$t('BONBOZ.TONG_QUAN.XEM_TUONG_TAC')"
        >
          <div
            v-if="dangTai"
            class="h-28 rounded-lg bg-n-alpha-2 animate-pulse"
          />
          <!-- Bốn ô ngăn nhau bằng VẠCH DỌC chứ không phải bằng bốn khung
               riêng: một khung chung nói rằng bốn con số này thuộc cùng một
               phép đếm, và ba con số sau cộng lại đúng bằng con số đầu. -->
          <div
            v-else
            class="grid grid-cols-2 border rounded-lg sm:grid-cols-4 border-n-weak divide-n-weak sm:divide-x"
          >
            <div
              v-for="o in oTuongTac"
              :key="o.khoa"
              v-tooltip.top="o.gt ? $t(o.gt) : null"
              class="flex flex-col justify-between p-3"
            >
              <div>
                <span
                  class="block text-2xl font-semibold tabular-nums text-n-slate-12"
                >
                  {{ o.so.toLocaleString() }}
                </span>
                <span class="block mt-0.5 text-xs truncate text-n-slate-11">
                  {{ $t(o.nhan) }}
                </span>
              </div>
              <div class="h-10 mt-2">
                <DuongNho :so="o.chuoi" :mau="o.mau" />
              </div>
            </div>
          </div>
        </SectionCard>

        <!-- Nhân viên -->
        <SectionCard
          :title="$t('BONBOZ.NHAN_VIEN.TIEU_DE')"
          :description="$t('BONBOZ.TONG_QUAN.NHAN_VIEN_MO_TA')"
          :to="{ name: 'agent_reports_index' }"
          :link-label="$t('BONBOZ.TONG_QUAN.XEM_NHAN_VIEN')"
        >
          <div
            v-if="dangTai"
            class="h-24 rounded-lg bg-n-alpha-2 animate-pulse"
          />
          <p v-else-if="!nhanVien.length" class="m-0 text-sm text-n-slate-11">
            {{ $t('BONBOZ.NHAN_VIEN.CHUA_CO') }}
          </p>
          <ul
            v-else
            class="grid gap-x-6 gap-y-4 p-0 m-0 list-none sm:grid-cols-2"
          >
            <li
              v-for="(n, i) in nhanVien"
              :key="n.id"
              class="flex items-center gap-3"
            >
              <span
                v-if="huyChuong(i)"
                class="block i-lucide-medal size-5 shrink-0"
                :style="{ color: huyChuong(i) }"
                aria-hidden="true"
              />
              <span v-else class="size-5 shrink-0" aria-hidden="true" />

              <span class="relative shrink-0">
                <Avatar :src="n.anh" :name="n.ten" :size="44" rounded-full />
                <!-- Số thứ hạng nằm trên ảnh, đúng như Pancake. `aria-hidden`
                     vì thứ hạng đã nằm trong thứ tự của danh sách. -->
                <span
                  class="absolute grid text-xs border rounded-full -bottom-1 -right-1 size-5 place-items-center bg-n-solid-2 border-n-weak tabular-nums text-n-slate-11"
                  aria-hidden="true"
                >
                  {{ i + 1 }}
                </span>
              </span>

              <span class="min-w-0">
                <span class="block text-sm truncate text-n-slate-12">
                  {{ n.ten }}
                </span>
                <span
                  class="flex items-center gap-1 mt-0.5 text-xs text-n-slate-11"
                >
                  <span
                    class="block i-lucide-message-square size-3.5"
                    aria-hidden="true"
                  />
                  {{ $t('BONBOZ.TONG_QUAN.N_LUOT', { n: n.luot }) }}
                </span>
              </span>
            </li>
          </ul>
        </SectionCard>

        <!-- Tổng đài + Theo giờ -->
        <div class="grid gap-4 sm:grid-cols-2 sm:items-start">
          <SectionCard
            :title="$t('BONBOZ.TONG_QUAN.TONG_DAI')"
            :description="$t('BONBOZ.TONG_QUAN.TONG_DAI_MO_TA')"
          >
            <div class="relative grid mx-auto size-40 place-items-center">
              <div class="absolute inset-0">
                <BieuDoTron :dong="miengTongDai" />
              </div>
              <div
                class="absolute inset-0 grid px-8 text-center pointer-events-none place-content-center"
              >
                <span class="text-xs leading-4 text-n-slate-11">
                  {{ $t('BONBOZ.TONG_QUAN.TONG_CUOC_GOI') }}
                </span>
                <span
                  class="mt-1 text-2xl font-semibold tabular-nums text-n-slate-12"
                >
                  {{ tongDai.tong.toLocaleString() }}
                </span>
              </div>
            </div>

            <p
              v-if="nhipTang(tongDai.thay_doi)"
              class="flex items-center justify-center gap-1 m-0 mt-2 text-xs"
              :class="nhipTang(tongDai.thay_doi).huong"
            >
              <span aria-hidden="true">{{
                nhipTang(tongDai.thay_doi).mui
              }}</span>
              {{ Math.abs(tongDai.thay_doi) }}%
            </p>

            <ul class="flex flex-col gap-2 p-0 m-0 mt-3 list-none">
              <li class="flex items-center gap-2">
                <span
                  class="block i-lucide-phone-incoming size-4 text-n-teal-11 shrink-0"
                  aria-hidden="true"
                />
                <span class="flex-1 text-sm text-n-slate-11">
                  {{ $t('BONBOZ.TONG_QUAN.GOI_DEN') }}
                </span>
                <span class="text-sm tabular-nums text-n-slate-12">
                  {{ tongDai.den }}
                </span>
              </li>
              <li class="flex items-center gap-2">
                <span
                  class="block i-lucide-phone-outgoing size-4 text-n-blue-11 shrink-0"
                  aria-hidden="true"
                />
                <span class="flex-1 text-sm text-n-slate-11">
                  {{ $t('BONBOZ.TONG_QUAN.GOI_DI') }}
                </span>
                <span class="text-sm tabular-nums text-n-slate-12">
                  {{ tongDai.di }}
                </span>
              </li>
            </ul>
          </SectionCard>

          <SectionCard
            :title="$t('BONBOZ.TONG_QUAN.THEO_GIO')"
            :description="$t('BONBOZ.TONG_QUAN.THEO_GIO_MO_TA')"
          >
            <div
              v-if="dangTai"
              class="rounded-lg h-52 bg-n-alpha-2 animate-pulse"
            />
            <div v-else class="h-52">
              <BieuDoTheoGio
                :diem="theoGio"
                :ten="$t('BONBOZ.TONG_QUAN.HOI_THOAI')"
              />
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  </div>
</template>
